import { useEffect, useRef } from 'react';

export function fitCanvas(canvas: HTMLCanvasElement, cssW: number, cssH: number) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = Math.floor(cssW * dpr);
  canvas.height = Math.floor(cssH * dpr);
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

export function canvasPoint(canvas: HTMLCanvasElement, clientX: number, clientY: number, worldW: number, worldH: number) {
  const r = canvas.getBoundingClientRect();
  return {
    x: ((clientX - r.left) / r.width) * worldW,
    y: ((clientY - r.top) / r.height) * worldH,
  };
}

export function useRafLoop(tick: (dt: number) => void) {
  const ref = useRef(tick);
  ref.current = tick;
  useEffect(() => {
    let id = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      ref.current(dt);
      id = requestAnimationFrame(loop);
    };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, []);
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(src));
    img.src = src;
  });
}

export async function loadImages(map: Record<string, string>) {
  const entries = Object.entries(map);
  const imgs = await Promise.all(
    entries.map(async ([k, src]) => {
      try {
        return [k, await loadImage(src)] as const;
      } catch {
        return [k, null] as const;
      }
    }),
  );
  const out = new Map<string, HTMLImageElement>();
  for (const [k, img] of imgs) if (img) out.set(k, img);
  return out;
}

export function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}

export function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

export function rand(a: number, b: number) {
  return a + Math.random() * (b - a);
}

export function pick<T>(arr: T[]): T {
  return arr[(Math.random() * arr.length) | 0];
}
